import { useEffect, useState } from "react";
import axios from "axios";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Col, Input, Modal, Row, Select } from "antd";
import useGet from "../../../global/hooks/useGet";
import { DealerColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  DeleteOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { billingTypeOptions } from "../../../utils/Common";

const initialFilterState = {
  DealerNo: "",
  OemNo: "",
  DealerName: "",
  GroupNo: "",
  BillingType: "",
};

export const Dealer = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "DealerNo",
    order: "desc",
  });
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [filterNoValueText, setFilterNoValueText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [unusedLoading, setUnusedLoading] = useState(false);
  const [showUnusedModal, setShowUnusedModal] = useState(false);
  const [unusedDealers, setUnusedDealers] = useState([]);

  const navigate = useNavigate();

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/dealers/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/dealers/`;
  const { data, loading, error, refetch } = useGet(url);
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/dealers/options/`;

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);
  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedGroups = getModifiedResponse(optionData, "groups");

  // const modifiedBillingCycles = getModifiedResponse(optionData, "bill_cycles");
  // const modifiedAutoBilling = getModifiedResponse(optionData, "auto_billings");

  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/dealers/bulk-delete/`;
  // selectedRowKeys.length > 1
  //   ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/dealers/delete/`)
  //   : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/dealers/delete/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    // deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(`/dealer/info/${record?.DealerNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      setFilterNoValueText("No value exists!");
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "DealerNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "DealerNo", // Make sure to specify sorting if necessary
      order: "desc",
    };
    setParams(params);
    // setSelectedRowKeys([]);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      // if (selectedRowKeys.length === 1) {
      //   await deleteData();
      // } else {
      await deleteMultiple({ ids: selectedRowKeys });
      // }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  const handleDeleteUnused = async () => {
    setUnusedLoading(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("userData"));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/dealers/unused/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token?.access_token}`,
          },
        },
      );
      const payload = response?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      if (!list.length) {
        openErrorNotification("No unused Dealers found.");
        return;
      }
      setUnusedDealers(list);
      setShowUnusedModal(true);
    } catch (error) {
      openErrorNotification(
        error?.response?.data?.message || "Something went wrong",
      );
    } finally {
      setUnusedLoading(false);
    }
  };

  const closeUnusedModal = () => {
    setShowUnusedModal(false);
    setUnusedDealers([]);
  };

  const onUnusedDeleteConfirm = async () => {
    const ids = unusedDealers
      .map((dealer) => dealer.DealerNo)
      .filter((id) => id !== undefined && id !== null);
    if (!ids.length) {
      closeUnusedModal();
      return;
    }
    try {
      await deleteMultiple({ ids });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    closeUnusedModal();
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");

      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.DealerNo,
      }));
      setListData(newData);
    }
    // setSelectedRowKeys([]);
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Dealer",
      href: "/dealer",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        {/* Filter part */}
        <div className="mb-4">
          <div className="mt-2">
            <Row gutter={[16, 16]}>
              {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Input
                  className="input_text_3 mr10 !h-full"
                  name="DealerNo"
                  placeholder="No"
                  value={filter?.DealerNo}
                  onChange={handleFilterChange}
                />
              </Col> */}
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Name"
                  value={filter.DealerName}
                  onChange={handleFilterChange}
                  name="DealerName"
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Select
                  showSearch
                  placeholder="Group"
                  optionFilterProp="label"
                  value={
                    filter.GroupNo || typeof filter.GroupNo === "number"
                      ? Number(filter.GroupNo)
                      : null
                  }
                  onChange={(value) => setFilter({ ...filter, GroupNo: value })}
                  // onSearch={onSearch}
                  options={modifiedGroups}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Select
                  showSearch
                  placeholder="Billing Type"
                  optionFilterProp="label"
                  value={
                    filter.BillingType || typeof filter.BillingType === "number"
                      ? Number(filter.BillingType)
                      : null
                  }
                  onChange={(value) =>
                    setFilter({ ...filter, BillingType: value })
                  }
                  // onSearch={onSearch}
                  options={billingTypeOptions}
                  style={{ width: "100%" }}
                />
              </Col>
              {![4, 5, 6].includes(logData?.role_value) && (
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    showSearch
                    placeholder="OEM"
                    optionFilterProp="label"
                    value={
                      filter.OemNo || typeof filter.OemNo === "number"
                        ? Number(filter.OemNo)
                        : null
                    }
                    onChange={(value) => setFilter({ ...filter, OemNo: value })}
                    // onSearch={onSearch}
                    options={modifiedOems}
                    style={{ width: "100%" }}
                  />
                </Col>
              )}

              {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="Billing Cycle"
                  optionFilterProp="BillingCycle"
                  value={filter.BillingCycle || undefined}
                  onChange={(value) =>
                    setFilter({ ...filter, BillingCycle: value })
                  }
                  options={modifiedBillingCycles}
                  style={{ width: "100%" }}
                />
              </Col> */}
              {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="Auto Billing"
                  optionFilterProp="AutoBilling"
                  value={filter.AutoBilling || undefined}
                  onChange={(value) =>
                    setFilter({ ...filter, AutoBilling: value })
                  }
                  options={modifiedAutoBilling}
                  style={{ width: "100%" }}
                />
              </Col> */}
            </Row>
          </div>

          <div className="w-full overflow-x-auto">
            <div className="flex justify-between my-4 flex-nowrap min-w-[760px]">
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleFilter}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  Apply
                </Button>
                <Button className="reset-button" onClick={handleReset}>
                  <HiArrowPath className="!text-[14px] font-medium" />
                  Reset
                </Button>
                <Button
                  type="primary"
                  className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                  onClick={() => navigate("/dealer/add")}
                >
                  <PlusOutlined />
                  Add
                </Button>
                <Button
                  type="primary"
                  disabled={selectedRowKeys?.length === 0 ? true : false}
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <DeleteOutlined />
                  Delete
                </Button>
                {/* <Button
                  type="primary"
                  className="mr-2 min-w-[104px] h-[30px] font-medium"
                  loading={unusedLoading}
                  onClick={handleDeleteUnused}
                >
                  <DeleteOutlined />
                  Delete Unused
                </Button> */}
              </div>
              {/* <div>
                <Button
                  type="primary"
                  className="mr-2 w-[30px] h-[30px]"
                  onClick={() => navigate("/dealer/add")}
                >
                  <PlusOutlined />
                </Button>
                <Button
                  type="primary"
                  disabled={selectedRowKeys?.length === 0 ? true : false}
                  className="w-[30px] h-[30px]"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <DeleteOutlined />
                </Button>
              </div> */}
            </div>
          </div>
          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <Modal
            open={showUnusedModal}
            onCancel={closeUnusedModal}
            closable={false}
            width={420}
            centered
            footer={null}
          >
            <div className="flex pt-4 pb-2 flex-col font-['Poppins'] w-full">
              <div className="flex items-start gap-x-4 w-full px-4">
                <span className="flex items-center justify-center w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex-shrink-0 mt-0.5">
                  <WarningOutlined className="text-2xl" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium leading-6 text-black m-0">
                    Are you sure you want to Delete {unusedDealers.length}{" "}
                    unused dealer
                    {unusedDealers.length === 1 ? "" : "s"}?
                  </p>
                  <p className="text-[13px] leading-5 text-[#5b6b7a] mt-1.5 mb-0">
                    These Dealers are not associated with any device and will be
                    permanently deleted.
                  </p>
                </div>
              </div>

              <div className="mt-3 mx-4 rounded-md border border-[#e5e7eb] overflow-hidden">
                <div className="max-h-[240px] overflow-y-auto">
                  <div className="grid grid-cols-[64px_1fr] gap-x-2 px-2.5 py-1.5 bg-[#f4f5f9] text-[11px] font-medium text-[#6b7280] sticky top-0 z-10">
                    <span>No</span>
                    <span>Name</span>
                  </div>
                  {unusedDealers.map((dealer) => (
                    <div
                      key={dealer.DealerNo}
                      className="grid grid-cols-[64px_1fr] gap-x-2 px-2.5 py-1.5 text-[13px] text-black border-t border-[#eef0f3]"
                    >
                      <span className="text-[#374151]">{dealer.DealerNo}</span>
                      <span className="truncate">
                        {dealer.DealerName || dealer.Dealer || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-4 w-full px-1">
                <button
                  onClick={onUnusedDeleteConfirm}
                  className="px-6 py-1 text-[14px] bg-[#007BFF] text-white rounded-md hover:bg-white hover:text-[#007BFF] transition shadow-none hover:shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
                >
                  Yes
                </button>
                <button
                  onClick={closeUnusedModal}
                  className="px-6 py-1 text-[14px] text-white bg-red-600 rounded-md hover:bg-white hover:text-red-600 transition shadow-none hover:shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
                >
                  No
                </button>
              </div>
            </div>
          </Modal>
          <GlobalTable
            columns={DealerColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              logData?.role_value === 4 ||
                logData?.role_value === 5 ||
                logData?.role_value === 6,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            // onRow={onRow}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
